// © 2026 Orpyx Medical Technologies Inc.

import {PatientActivityLogTableItemProps} from "./patient-activity-log-table-item.interface";
import React, {memo} from "react";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import {AdvancedIconButton, DateFormatPipe, DateTimePipe} from "orpyx-web-common";
import {PatientsListActionRequiredAlerts} from "../../../../patients-list/components/patients-list-action-required-alerts/patients-list-action-required-alerts";
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import {CircularProgress, Tooltip} from "@material-ui/core";
import {ImageUrlPipe} from "../../../../../../shared/pipes/image-url-pipe";
import { TimeSpan } from "modules/shared/utility/timeSpan";
import usePatientActivityLogTableItemStyle from "./patient-activity-log-table-item.style";
import { ImageSearchOutlined } from "@material-ui/icons";
import {PatientActivityLogSuggestedAction} from "../patient-activity-log-suggested-action/patient-activity-log-suggested-action";

/* Abbreviated rather than the shared DatePipe's "MMMM DD, YYYY". The cell is nowrap, so the
   longest month name sets the column's width for every row; the tooltip still carries the
   unabbreviated date and time. */
const ActivityLogDateFormat = 'MMM DD, YYYY';

// Required to preserve the line breaks entered in the original note
const parseNoteTextLineBreaks = (text: string | null | undefined, classes: any) => {
    return text?.split(/\n/).map((line, index) => <p key={index} className={classes.noMargin}>{line}<br /></p>)
}

export const PatientActivityLogTableItem = memo((props: PatientActivityLogTableItemProps) => {
    const careNote = props.careNote;
    const classes = usePatientActivityLogTableItemStyle();

    // Absent on notes whose action offers no concern, and on notes written before primary concerns existed
    const primaryConcernName = careNote.primaryConcernName;
    const hasPrimaryConcern = !!primaryConcernName;

    return <TableRow>
        <TableCell>
            <div className={props.classes.actionCol}>
                <PatientsListActionRequiredAlerts alerts={careNote.actionRequiredAlerts!}/>
            </div>
        </TableCell>

        <TableCell className={props.classes.nowrap}>
            <Tooltip title={DateTimePipe(careNote.noteDate)!}>
                <span>
                    {DateFormatPipe(careNote.noteDate, ActivityLogDateFormat)!}
                </span>
            </Tooltip>
        </TableCell>

        <TableCell className={props.classes.noteText}>
            {parseNoteTextLineBreaks(careNote.noteText, classes)}

            {/* Absent on notes without a concern, on concerns that offer no suggested actions, and
                on notes written before suggested actions were a fixed list */}
            {careNote.suggestedActionTypeId != null &&
            <PatientActivityLogSuggestedAction
                classes={props.classes}
                suggestedActionTypeId={careNote.suggestedActionTypeId}
                suggestedActionText={careNote.suggestedActionText ?? undefined}
                suggestedActionOwner={careNote.suggestedActionOwner ?? undefined}
            />}
        </TableCell>

        <TableCell className={props.classes.actionIconCell}>
            {careNote.carePractitionerAction?.url &&
            <Tooltip title={careNote.carePractitionerAction?.name!}>
                <img
                    alt={careNote.carePractitionerAction?.name!}
                    src={ImageUrlPipe(careNote.carePractitionerAction?.url)}
                />
            </Tooltip>}

            {/* Matches the concern line in the label cell so the icon stays level with the
                action name while the pair as a whole remains centred in the row */}
            {hasPrimaryConcern &&
            <span className={props.classes.actionIconSubLabelSpacer} aria-hidden="true">&nbsp;</span>}
        </TableCell>

        <TableCell className={props.classes.actionLabelCell}>
            {careNote.carePractitionerAction?.name}
            {hasPrimaryConcern &&
            <span className={props.classes.actionSubLabel}>{primaryConcernName}</span>}
        </TableCell>

        <TableCell className={props.classes.nowrap}>
            {careNote.carePractitionerFullName}
        </TableCell>

        <TableCell>
            {TimeSpan.toHoursAndMinutesAndSeconds(careNote.manualTime ?? careNote.trackingTime)}
        </TableCell>

        <TableCell align="center">
            {
            props.downloadingInsight ? <CircularProgress size={20}/>
            : careNote.hasInsightUrl && 
            <AdvancedIconButton
                disabled={props.downloadingInsight}
                tooltip={`Download daily insight figure as it appeared at ${DateTimePipe(careNote.noteDate)}`}
                icon={ImageSearchOutlined}
                onClick={
                    () => props.onClickedDownloadInsightFigure(careNote)}
            />}
        </TableCell>
        <TableCell align="center">
            {
            props.inProcess ? <CircularProgress size={20} />
            : careNote.carePractitionerAction?.hasReport &&
            <AdvancedIconButton

                disabled={props.inProcess}
                tooltip={`Download report at ${DateTimePipe(careNote.noteDate)}`}
                icon={GetAppOutlinedIcon}
                onClick={() => props.onClickedDownloadPatientActivityLog(careNote)}
            />}
        </TableCell>
    </TableRow>
});
